import React from 'react';

import caretDownTokens from '@amzn/meridian-tokens/base/icon/caret-down';
import caretUpTokens from '@amzn/meridian-tokens/base/icon/caret-up';
import metricsTokens from '@amzn/meridian-tokens/base/icon/metrics';
import minusTokens from '@amzn/meridian-tokens/base/icon/minus';
import viewTokens from '@amzn/meridian-tokens/base/icon/view';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Tooltip from '@amzn/meridian/tooltip';

import { ColDef } from 'ag-grid-community';
import { emptyCellFormatter } from 'src/util/grid/cellFormatter';
import { getTerritoryFlagRenderer } from 'src/util/grid/cellRenderer';

export const getExecutionRequestDetailsColDefs = (
    onViewRoyalties: (data: any) => void,
    isRoyaltyDataLoaded: boolean
): ColDef[] => [
    {
        headerName: 'Actions',
        field: 'actions',
        cellRenderer: (params: any) => (
            <Column height="100%" alignmentVertical="center">
                <Tooltip title="View Royalties" position="top">
                    <Button
                        aria-label="view royalty details"
                        type="link"
                        size="small"
                        disabled={!isRoyaltyDataLoaded}
                        onClick={() => onViewRoyalties(params.data)}
                    >
                        <Icon className="button-icon" tokens={viewTokens} />
                    </Button>
                </Tooltip>
            </Column>
        ),
        width: 120,
        pinned: 'right',
        sortable: false,
        filter: false,
        suppressSizeToFit: true
    },
    {
        headerName: 'Service',
        field: 'service',
        valueFormatter: emptyCellFormatter,
        filter: 'agSetColumnFilter',
        pinned: 'left'
    },
    {
        headerName: 'Territory',
        field: 'territory',
        filter: 'agSetColumnFilter',
        valueFormatter: emptyCellFormatter,
        cellRenderer: getTerritoryFlagRenderer,
        pinned: 'left'
    },
    {
        headerName: 'Entity',
        field: 'entity',
        valueFormatter: emptyCellFormatter,
        filter: 'agSetColumnFilter',
        pinned: 'left'
    },
    {
        headerName: 'Month',
        field: 'month',
        valueFormatter: emptyCellFormatter,
        filter: 'agSetColumnFilter',
        pinned: 'left'
    },
    {
        headerName: 'Catalog Code',
        field: 'catalogVendorCode',
        valueFormatter: emptyCellFormatter,
        filter: 'agSetColumnFilter'
    },
    {
        headerName: 'Royalty Code',
        field: 'royaltyVendorCode',
        valueFormatter: emptyCellFormatter,
        filter: 'agSetColumnFilter'
    },
    {
        headerName: 'Currency',
        field: 'afterCurrency',
        filter: 'agSetColumnFilter',
        valueFormatter: emptyCellFormatter,
        minWidth: 120
    },
    {
        headerName: 'Prior Amount',
        field: 'beforeAmount',
        valueFormatter: (params) => {
            if (params.value == null) return '';
            return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(params.value);
        },
        filter: 'agNumberColumnFilter'
    },
    {
        headerName: 'Current Amount',
        field: 'afterAmount',
        valueFormatter: (params) => {
            if (params.value == null) return '';
            return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(params.value);
        },
        filter: 'agNumberColumnFilter'
    },
    {
        headerName: 'Delta',
        field: 'changeAmount',
        valueGetter: (params: any) => {
            const beforeAmount = params.data.beforeAmount;
            const afterAmount = params.data.afterAmount;

            if (beforeAmount == null || afterAmount == null) return null;

            return afterAmount - beforeAmount;
        },
        cellRenderer: (params: any) => {
            const beforeAmount = params.data.beforeAmount;
            const afterAmount = params.data.afterAmount;

            if (beforeAmount == null || afterAmount == null) return '';

            const difference = afterAmount - beforeAmount;
            const color = difference > 0 ? '#00a650' : difference < 0 ? '#d13212' : '#000';
            const formattedDiff = new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                signDisplay: 'always'
            }).format(difference);

            return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <span style={{ color, fontWeight: 500 }}>{formattedDiff}</span>
                </div>
            );
        },
        width: 120,
        sortable: true,
        filter: false,
        suppressSizeToFit: true
    },
    {
        headerName: 'Delta %',
        field: 'changePercent',
        valueGetter: (params: any) => {
            const beforeAmount = params.data.beforeAmount;
            const afterAmount = params.data.afterAmount;

            if (beforeAmount == null || afterAmount == null || beforeAmount === 0) return null;

            return ((afterAmount - beforeAmount) / Math.abs(beforeAmount)) * 100;
        },
        cellRenderer: (params: any) => {
            const beforeAmount = params.data.beforeAmount;
            const afterAmount = params.data.afterAmount;

            if (beforeAmount == null || afterAmount == null || beforeAmount === 0) return '';

            const percentChange = ((afterAmount - beforeAmount) / Math.abs(beforeAmount)) * 100;
            const color = percentChange > 0 ? '#00a650' : percentChange < 0 ? '#d13212' : '#000';
            const formattedPercent = new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                signDisplay: 'always'
            }).format(percentChange);

            return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <span style={{ color, fontWeight: 500 }}>{formattedPercent}%</span>
                </div>
            );
        },
        width: 120,
        sortable: true,
        filter: false,
        suppressSizeToFit: true
    }
];
